import { useNavigate } from 'react-router-dom';
import { Box, Typography, Tabs, Tab, Button } from '@mui/material';
import { AssignmentTurnedIn, Lock, Timer } from '@mui/icons-material';
import { AdminRoles, DoctorRoles, PatientDataMode, Phase } from '../../config/DiactiveTypes';
import { getPatientHspId, formatTimestamp, formatDateTime, getPatientGroupName, determinePatientStatus, minDaysForPostEval } from '../../utils/DiactiveUtils';
import { getHospitalName } from '../../services/HospitalService';
import SendEmailButton from '../common/SendEmailButton';
import DetailRow from '../common/DetailRow';
import PatientDetailsConfig from '../../data/PatientDetailsConfig.json';

const PatientDataTable = ({
  authUserRole,
  userData,
  preEvalData,
  postEvalData,
  phaseTab,
  showPersonal,
  isReadyForAppAccess,
  isReadyToStartCountdown,
  workouts,
  isMobile,
  isDoctorPreviewMode = false,
  onDoctorPreviewBlockedAction = () => {},
  onPhaseTabChange,
  onSendAppAccess,
  onStartCountdown,
  onRemoveFromStudy
}) => {
  const navigate = useNavigate();

  const evalData = phaseTab === Phase.PRE ? preEvalData : postEvalData;
  const isPostEvalFilled = Object.keys(postEvalData).length > 0;
  const isReadyForPostEval = !isPostEvalFilled && userData.daysRemaining <= minDaysForPostEval;

  // Get field value based on configuration
  const getFieldValue = (field) => {
    let value = null;
    if (evalData[field.name] !== undefined) {
      value = evalData[field.name];
    }
    else if (userData[field.name] !== undefined) {
      value = userData[field.name];
    }

    // Handle special cases
    switch (field.name) {
      case 'id':
        return getPatientHspId(userData);
      case 'email':
        return showPersonal ? userData.email : "-";
      case 'hospital':
        return getHospitalName(userData.hospital);
      case 'doctorName':
        return evalData.evalCreatedByName ?? userData.doctorName ?? '—';
      case 'role':
        return getPatientGroupName(userData.role);
      case 'status':
        const status = determinePatientStatus(
          userData.countdownStartedAt,
          userData.daysRemaining,
          userData.hasFilledQsPre,
          userData.hasFilledQsPost,
          userData.glucoseFileCount,
          userData.isInStudy
        );
        return status.label;
      case 'sessionsCount':
        return workouts.length;
      case 'lastSessionDate':
        if (workouts.length > 0) {
          return formatDateTime(workouts[workouts.length - 1].createdAt);
        }
        return "-";
      case 'countdownStartedAt':
        if (!userData.countdownStartedAt) return "-";
        return formatDateTime(userData.countdownStartedAt);
      case 'basalInsulinDistribution':
        return `${evalData.basalInsulinPercentage}% - ${100 - evalData.basalInsulinPercentage}%`;
      case 'percentile':
        return `${value}%`;
      case 'removedFromStudyByName':
        return userData.removedFromStudyByName ?? '—';
      default:
        break;
    }

    // Handle optional fields
    if (field.optional && (value === undefined || value === null || value === "")) {
      return "-";
    }

    // Apply formatting based on type
    switch (field.type) {
      case 'date':
        if (value && value.toDate) {
          value = formatTimestamp(value);
        }
        break;
      case 'datetime':
        if (value && value.toDate) {
          value = formatDateTime(value);
        }
        break;
      case 'select':
        if (value === "ManualEntry" && field.options) {
          const customFieldName = `custom${field.name.charAt(0).toUpperCase() + field.name.slice(1)}`;
          const customValue = evalData[customFieldName];

          if (customValue) {
            value = customValue; // Use the custom value instead of "Otro (ingreso manual)"
          } else {
            // Fallback to the label from options
            const option = field.options.find(opt => opt.value === value);
            value = option && option.label ? option.label : value;
          }
        } else if (field.options && value !== undefined) {
          // Normal select field mapping
          const option = field.options.find(opt => opt.value === value);
          value = option && option.label ? option.label : value;
        }
        break;
      case 'number':
        if (value) {
          value = value.toString().replace(".", ",");
        }
        break;
    }

    // Add unit if specified
    if (field.unit && value !== "-") {
      value = `${value} ${field.unit}`;
    }

    return value;
  };

  // Check if field should be shown
  const shouldShowField = (field) => {
    // Check showWhen condition first
    if (field.showWhen) {
      switch (field.showWhen) {
        case 'showPersonal':
          if (!showPersonal) return false;
          break;
        case '!showPersonal':
          if (showPersonal) return false;
          break;
        case '!userData.isInStudy':
          if (userData.isInStudy) return false;
          break;
        default:
          break;
      }
    }

    // Always hide custom fields (they're handled by their parent fields)
    if (field.name.startsWith('custom')) {
      return false;
    }

    // Check dependency condition
    if (field.dependency) {
      const dependencyFieldValue = evalData[field.dependency.field];

      if (dependencyFieldValue !== field.dependency.value) {
        return false;
      }
    }

    return true;
  };

  const onPostEvalClick = () => {
    if (isDoctorPreviewMode) {
      onDoctorPreviewBlockedAction();
      return;
    }
    navigate(`/patient/${PatientDataMode.Eval}/${Phase.POST}/${userData.id}`);
  };

  // Render a single step
  const renderStep = (step) => {
    const visibleFields = step.fields.filter(shouldShowField);

    // Split fields into two columns
    const midPoint = Math.ceil(visibleFields.length / 2);
    const leftColumn = visibleFields.slice(0, midPoint);
    const rightColumn = visibleFields.slice(midPoint);

    return (
      <Box key={step.title} sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {step.title}
        </Typography>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: { xs: 0, md: 6 }
        }}>
          <Box>
            {leftColumn.map((field) => (
              <DetailRow
                key={field.name}
                label={field.label}
                value={getFieldValue(field)}
              />
            ))}
          </Box>

          <Box>
            {rightColumn.map((field) => (
              <DetailRow
                key={field.name}
                label={field.label}
                value={getFieldValue(field)}
              />
            ))}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      {AdminRoles.includes(authUserRole) && (
        <Box sx={{ display: 'flex', justifyContent: isReadyToStartCountdown || isReadyForAppAccess ? 'space-between' : 'flex-end', mb: 3 }}>
          {isReadyToStartCountdown && (
            <SendEmailButton
              text="Comenzar Cuenta Atrás"
              startIcon={<Timer />}
              onClick={onStartCountdown}
              disabled={userData.glucoseFileCount.pre === 0 || !userData.hasFilledQsPre}
            />
          )}
          {isReadyForAppAccess && (
            <SendEmailButton
              text="Otorgar Acceso App"
              onClick={onSendAppAccess}
              disabled={userData.glucoseFileCount.post === 0 || !isPostEvalFilled || !userData.hasFilledQsPost}
            />
          )}
          <SendEmailButton
            text="Dar de Baja del Estudio"
            isErrorButton={true}
            onClick={onRemoveFromStudy}
            disabled={!userData.isInStudy}
          />
        </Box>
      )}

      {DoctorRoles.includes(authUserRole) && !isPostEvalFilled && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<AssignmentTurnedIn />}
            onClick={onPostEvalClick}
            disabled={!isReadyForPostEval}
          >
            Evaluación Post
          </Button>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Tabs
          value={phaseTab}
          onChange={onPhaseTabChange}
          variant="standard"
          sx={{ mb: 3 }}
        >
          <Tab
            label="Evaluación PRE"
            value={Phase.PRE}
            style={{fontSize: isMobile ? '0.75em' : '1em'}}
          />
          <Tab
            label="Evaluación POST"
            value={Phase.POST}
            icon={!isPostEvalFilled ? <Lock fontSize="10" /> : null}
            iconPosition="start"
            disabled={!isPostEvalFilled}
            style={{fontSize: isMobile ? '0.75em' : '1em'}}
          />
        </Tabs>
      </Box>

      <Box>
        {PatientDetailsConfig.steps.map(renderStep)}
      </Box>
    </Box>
  );
};

export default PatientDataTable;